import { FC, useRef } from "react";
import { Box, InputBase, Button, Paper, IconButton, Typography } from "@mui/material";
import { AutoAwesome } from "@mui/icons-material";
import { FiPlus, FiArrowUp } from "react-icons/fi";
import { ChatCategory } from "@/types/chat";
import ChatAgentSelector from "@/components/chat-agent-selector";

interface ChatInputAreaProps {
  value: string;
  onChange: (val: string) => void;
  onSend: () => void;
  onKeyPress: (e: any) => void;
  isLoading: boolean;
  category: ChatCategory;
  onCategoryChange: (cat: ChatCategory) => void;
  hasMessages?: boolean;
}

const ChatInputArea: FC<ChatInputAreaProps> = ({
  value,
  onChange,
  onSend,
  onKeyPress,
  isLoading,
  category,
  onCategoryChange,
  hasMessages = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isMessageEmpty = value.trim() === "";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: "800px",
        px: 2,
        pb: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          border: "1px solid #E6E7E7",
          borderRadius: "10px",
          p: 1.5,
          backgroundColor: "#FFFFFF",
        }}
      >
        {/* Input Field */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Box sx={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
             <AutoAwesome sx={{ fontSize: 16, color: "#687071" }} />
          </Box>
          <InputBase
            inputRef={inputRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyPress}
            placeholder={isLoading ? "AI is responding..." : "Ask anything..."}
            disabled={isLoading}
            multiline
            maxRows={4}
            sx={{
              flex: 1,
              fontFamily: "Inter, sans-serif",
              fontSize: "14px",
              color: "#031112", // Black text instead of #687071
            }}
          />
        </Box>

        {/* Bottom Toolbar */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
             {/* Plus Button */}
            <IconButton
              size="small"
              sx={{
                border: "1px solid #E8EBED",
                borderRadius: "8px",
                p: 0.5,
              }}
            >
              <FiPlus color="#687071" size={18} />
            </IconButton>

            {/* Agent Selector */}
            <ChatAgentSelector
                category={category}
                onCategoryChange={onCategoryChange}
                dense
                disabled={isLoading || hasMessages}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="contained"
              onClick={onSend}
              disabled={isMessageEmpty || isLoading}
              sx={{
                background: "linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(90deg, #0B494B 0%, #0B494B 100%)",
                borderRadius: "8px",
                color: "white",
                minWidth: "unset",
                p: "6px 12px",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                    background: "linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(90deg, #093B3D 0%, #093B3D 100%)",
                },
                "&.Mui-disabled": {
                    opacity: 0.6,
                    background: "#E8EBED",
                    color: "#8E9494",
                }
              }}
            >
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                 <FiArrowUp size={18} />
                 <Typography sx={{ color: "inherit", fontWeight: 500, fontSize: "14px" }}>Send</Typography>
               </Box>
            </Button>
          </Box>
        </Box>
      </Paper>
      
      <Box sx={{ display: "flex", gap: 0.5, mt: 1 }}>
        <Typography sx={{ fontSize: "12px", color: "#8E9494" }}>
            Riffs AI may display inaccurate info, so please double check the response.
        </Typography>
        <a href="https://tryriffs.com/privacy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <Typography sx={{ fontSize: "12px", color: "#33404D", textDecoration: "underline", fontWeight: 500, cursor: "pointer" }}>
                Your Privacy & Riffs Intelligence
            </Typography>
        </a>
      </Box>
    </Box>
  );
};

export default ChatInputArea;

